import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { registerBackDialog } from './nativeBack.ts';
import { useIsMobile } from './hooks.ts';
import { tr } from '../i18n/i18n.ts';

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; icon?: ReactNode }>(function Button(
  { variant = 'secondary', icon, className = '', children, ...rest },
  ref,
) {
  const styles: Record<BtnVariant, string> = {
    primary: 'bg-accent text-on-accent hover:bg-accent-strong font-semibold',
    secondary: 'border border-border-strong bg-raised text-text hover:border-accent',
    ghost: 'text-text hover:bg-raised',
    danger: 'border border-risk/70 text-risk hover:bg-risk-bg',
  };
  return (
    <button
      ref={ref}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${styles[variant]} ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});

export function IconButton({ label, children, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button aria-label={label} title={label} className={`grid min-h-11 min-w-11 place-items-center rounded-lg text-muted hover:bg-raised hover:text-text ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function Field({ label, hint, error, children, htmlFor }: { label: string; hint?: string; error?: string | null; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-text">
        {label}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted">{hint}</p>}
      {error && (
        <p className="text-xs font-medium text-risk" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export const inputCls = 'min-h-11 w-full rounded-lg border border-border-strong bg-bg px-3 text-sm text-text placeholder:text-muted focus:border-accent';

export function Panel({ children, className = '', as: As = 'section', ...rest }: { children: ReactNode; className?: string; as?: 'section' | 'div' | 'article' } & Record<string, unknown>) {
  return (
    <As className={`rounded-[var(--radius-panel)] border border-border bg-panel ${className}`} {...rest}>
      {children}
    </As>
  );
}

export function Badge({ children, tone = 'neutral', title }: { children: ReactNode; tone?: 'neutral' | 'risk' | 'warn' | 'ok' | 'source' | 'accent' | 'info'; title?: string }) {
  const t: Record<string, string> = {
    neutral: 'border-border-strong text-muted',
    risk: 'border-risk/70 text-risk bg-risk-bg',
    warn: 'border-warn/60 text-warn bg-warn-bg',
    ok: 'border-ok/60 text-ok',
    source: 'border-source/60 text-source',
    accent: 'border-accent/60 text-accent',
    info: 'border-info/60 text-info',
  };
  return (
    <span title={title} className={`inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5 text-left text-xs font-medium [overflow-wrap:anywhere] ${t[tone]}`}>
      {children}
    </span>
  );
}

/**
 * Responsivt ark: bottom sheet på mobil, dialog (eller sidopanel) på större skärm.
 * Radix Dialog ger fokusfälla, Esc, återställt fokus och korrekta ARIA-attribut.
 */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = false,
  wide = false,
  hideClose = false,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  side?: boolean;
  wide?: boolean;
  /** För dialoger som kräver ett val (t.ex. första start). */
  hideClose?: boolean;
}) {
  const mobile = useIsMobile();
  const change = useRef(onOpenChange);
  change.current = onOpenChange;
  useEffect(() => {
    if (open) return registerBackDialog(() => change.current(false));
  }, [open]);
  // Återställ fokus till det som hade fokus när arket öppnades (arken öppnas programmatiskt och avmonteras).
  const [returnTo] = useState(() => (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null));
  useEffect(() => () => {
    const el = returnTo;
    if (el && el !== document.body) requestAnimationFrame(() => el.isConnected && el.focus());
  }, [returnTo]);
  const pos = mobile
    ? 'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-2xl border-t'
    : side
      ? 'right-0 top-0 h-full w-[420px] max-w-full border-l'
      : 'left-1/2 top-1/2 max-h-[88dvh] -translate-x-1/2 -translate-y-1/2 rounded-2xl border';
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          className={`autoday-sheet fixed z-50 flex flex-col border-border-strong bg-panel text-text shadow-2xl focus:outline-none ${pos}`}
          style={!mobile && !side ? { width: `min(${wide ? 760 : 560}px, calc(100vw - 2rem))` } : undefined}
        >
          {mobile && <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-border-strong" aria-hidden />}
          <div className="flex shrink-0 items-start gap-3 border-b border-border px-5 py-3">
            <div className="flex-1">
              <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-0.5 text-sm text-muted">{description}</Dialog.Description> : <Dialog.Description className="sr-only">{title}</Dialog.Description>}
            </div>
            {!hideClose && (
              <Dialog.Close asChild>
                <IconButton label={tr('Stäng', 'Close')}>
                  <X size={20} aria-hidden />
                </IconButton>
              </Dialog.Close>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="autoday-safe-bottom flex flex-wrap justify-end gap-2 border-t border-border px-5 pt-3">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-xl border border-dashed border-border-strong p-5">
      <p className="font-medium">{title}</p>
      {children && <div className="text-sm text-muted">{children}</div>}
      {action}
    </div>
  );
}
